import React, { useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';

const StaggeredMenu = ({
  isOpen,
  onClose,
  onOpen,
  items = [],
  position = 'right',
  colors = ['#0c0c0e', '#111113'],
  accentColor = '#14b8a6',
  displayItemNumbering = true,
  children,
}) => {
  const onToggle = useCallback(() => {
    if (isOpen) {
      onClose?.();
    } else {
      onOpen?.();
    }
  }, [isOpen, onClose, onOpen]);
  const location = useLocation();
  const panelRef = useRef(null);
  const preLayersRef = useRef(null);
  const preLayerElsRef = useRef([]);
  const openTlRef = useRef(null);
  const closeTweenRef = useRef(null);
  const prevOpenRef = useRef(false);

  const plusHRef = useRef(null);
  const plusVRef = useRef(null);
  const iconRef = useRef(null);
  const spinTweenRef = useRef(null);

  const toggleBtnRef = useRef(null);

  // Close on route change
  useEffect(() => {
    if (isOpen) onClose?.();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const panel = panelRef.current;
      const preContainer = preLayersRef.current;
      const plusH = plusHRef.current;
      const plusV = plusVRef.current;
      const icon = iconRef.current;

      if (!panel || !plusH || !plusV || !icon) return;

      let preLayers = [];
      if (preContainer) {
        preLayers = Array.from(preContainer.querySelectorAll('.sm-prelayer'));
      }
      preLayerElsRef.current = preLayers;

      const offscreen = position === 'left' ? -100 : 100;
      gsap.set([panel, ...preLayers], { xPercent: offscreen });

      gsap.set(plusH, { transformOrigin: '50% 50%', rotate: 0 });
      gsap.set(plusV, { transformOrigin: '50% 50%', rotate: 90 });
      gsap.set(icon, { rotate: 0, transformOrigin: '50% 50%' });
    });
    return () => ctx.revert();
  }, [position]);

  const buildOpenTimeline = useCallback(() => {
    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return null;

    openTlRef.current?.kill();
    closeTweenRef.current?.kill();
    closeTweenRef.current = null;

    const itemEls = Array.from(panel.querySelectorAll('.sm-panel-itemLabel'));
    const numberEls = Array.from(panel.querySelectorAll('.sm-panel-list[data-numbering] .sm-panel-item'));
    const extraEls = Array.from(panel.querySelectorAll('.sm-extra-content'));

    const layerStates = layers.map(el => ({ el, start: Number(gsap.getProperty(el, 'xPercent')) }));
    const panelStart = Number(gsap.getProperty(panel, 'xPercent'));

    if (itemEls.length) gsap.set(itemEls, { yPercent: 140, rotate: 10 });
    if (numberEls.length) gsap.set(numberEls, { '--sm-num-opacity': 0 });
    if (extraEls.length) gsap.set(extraEls, { y: 20, opacity: 0 });

    const tl = gsap.timeline({ paused: true });

    layerStates.forEach((ls, i) => {
      tl.fromTo(ls.el, { xPercent: ls.start }, { xPercent: 0, duration: 0.5, ease: 'power4.out' }, i * 0.07);
    });

    const lastTime = layerStates.length ? (layerStates.length - 1) * 0.07 : 0;
    const panelInsertTime = lastTime + (layerStates.length ? 0.08 : 0);
    const panelDuration = 0.65;

    tl.fromTo(
      panel,
      { xPercent: panelStart },
      { xPercent: 0, duration: panelDuration, ease: 'power4.out' },
      panelInsertTime
    );

    if (itemEls.length) {
      const itemsStart = panelInsertTime + panelDuration * 0.15;
      tl.to(
        itemEls,
        { yPercent: 0, rotate: 0, duration: 1, ease: 'power4.out', stagger: { each: 0.1, from: 'start' } },
        itemsStart
      );

      if (numberEls.length) {
        tl.to(
          numberEls,
          { duration: 0.6, ease: 'power2.out', '--sm-num-opacity': 1, stagger: { each: 0.08, from: 'start' } },
          itemsStart + 0.1
        );
      }
    }

    if (extraEls.length) {
      const extrasStart = panelInsertTime + panelDuration * 0.5;
      tl.to(
        extraEls,
        { y: 0, opacity: 1, duration: 0.55, ease: 'power3.out', stagger: { each: 0.06, from: 'start' } },
        extrasStart
      );
    }

    openTlRef.current = tl;
    return tl;
  }, []);

  const playOpen = useCallback(() => {
    const tl = buildOpenTimeline();
    if (tl) tl.play(0);
  }, [buildOpenTimeline]);

  const playClose = useCallback(() => {
    openTlRef.current?.kill();
    openTlRef.current = null;

    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return;

    const all = [...layers, panel];
    closeTweenRef.current?.kill();

    const offscreen = position === 'left' ? -100 : 100;

    closeTweenRef.current = gsap.to(all, {
      xPercent: offscreen,
      duration: 0.32,
      ease: 'power3.in',
      overwrite: 'auto',
      onComplete: () => {
        const itemEls = Array.from(panel.querySelectorAll('.sm-panel-itemLabel'));
        if (itemEls.length) gsap.set(itemEls, { yPercent: 140, rotate: 10 });
        const numberEls = Array.from(panel.querySelectorAll('.sm-panel-list[data-numbering] .sm-panel-item'));
        if (numberEls.length) gsap.set(numberEls, { '--sm-num-opacity': 0 });
        const extraEls = Array.from(panel.querySelectorAll('.sm-extra-content'));
        if (extraEls.length) gsap.set(extraEls, { y: 20, opacity: 0 });
      }
    });
  }, [position]);

  const animateIcon = useCallback((opening) => {
    const icon = iconRef.current;
    const h = plusHRef.current;
    const v = plusVRef.current;
    if (!icon || !h || !v) return;

    spinTweenRef.current?.kill();

    if (opening) {
      gsap.set(icon, { rotate: 0, transformOrigin: '50% 50%' });
      spinTweenRef.current = gsap
        .timeline({ defaults: { ease: 'power4.out' } })
        .to(h, { rotate: 45, duration: 0.5 }, 0)
        .to(v, { rotate: -45, duration: 0.5 }, 0);
    } else {
      spinTweenRef.current = gsap
        .timeline({ defaults: { ease: 'power3.inOut' } })
        .to(h, { rotate: 0, duration: 0.35 }, 0)
        .to(v, { rotate: 90, duration: 0.35 }, 0)
        .to(icon, { rotate: 0, duration: 0.001 }, 0);
    }
  }, []);

  // React to isOpen changes from parent
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      playOpen();
      animateIcon(true);
    } else if (!isOpen && prevOpenRef.current) {
      playClose();
      animateIcon(false);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, playOpen, playClose, animateIcon]);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        toggleBtnRef.current &&
        !toggleBtnRef.current.contains(event.target) &&
        !event.target.closest('[data-menu-trigger]')
      ) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const currentPath = location.pathname;

  return (
    <div className="sm-scope fixed inset-0 pointer-events-none z-[70]">
      <button
        ref={toggleBtnRef}
        data-menu-trigger
        className="sm-toggle pointer-events-auto fixed top-[20px] right-[20px] z-[80] flex md:hidden items-center justify-center w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.1] backdrop-blur-md cursor-pointer transition-colors hover:bg-white/[0.1] text-white"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span
          ref={iconRef}
          className="relative w-[20px] h-[20px] shrink-0 inline-flex items-center justify-center"
          aria-hidden="true"
        >
          <span
            ref={plusHRef}
            className="absolute left-1/2 top-1/2 w-full h-[2.5px] bg-current rounded-[2px] -translate-x-1/2 -translate-y-1/2"
          />
          <span
            ref={plusVRef}
            className="absolute left-1/2 top-1/2 w-full h-[2.5px] bg-current rounded-[2px] -translate-x-1/2 -translate-y-1/2"
          />
        </span>
      </button>

      {/* Pre-layers for staggered color effect */}
      <div
        ref={preLayersRef}
        className="sm-prelayers absolute top-0 right-0 bottom-0 pointer-events-none z-[5]"
        aria-hidden="true"
      >
        {colors.map((c, i) => (
          <div
            key={i}
            className="sm-prelayer absolute top-0 right-0 h-full w-full"
            style={{ background: c }}
          />
        ))}
      </div>

      {/* Main Panel */}
      <aside
        ref={panelRef}
        className="sm-panel absolute top-0 right-0 h-full flex flex-col overflow-y-auto z-10 pointer-events-auto"
        style={{
          '--sm-accent': accentColor,
          background: 'rgba(8, 8, 10, 0.98)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
        }}
        aria-hidden={!isOpen}
      >
        {/* Panel header spacer */}
        <div className="pt-24 px-8" />

        {/* Nav items */}
        <div className="flex-1 flex flex-col px-8">
          <ul
            className="sm-panel-list list-none m-0 p-0 flex flex-col gap-2"
            role="list"
            data-numbering={displayItemNumbering || undefined}
          >
            {items.map((item, idx) => {
              const isActive = currentPath === item.path ||
                (item.path === '/' && currentPath === '/') ||
                (item.path !== '/' && currentPath.startsWith(item.path));

              return (
                <li className="relative overflow-hidden leading-none" key={item.label}>
                  <Link
                    to={item.path}
                    onClick={() => onClose?.()}
                    className="sm-panel-item relative font-semibold cursor-pointer leading-none tracking-[-1.5px] uppercase no-underline inline-block pr-[1.4em] transition-colors duration-150"
                    style={{
                      fontFamily: "'Qulio Display', 'Bodoni Moda', serif",
                      fontSize: 'clamp(2.5rem, 8vw, 3.5rem)',
                      color: isActive ? 'var(--sm-accent)' : 'rgba(255,255,255,0.85)',
                    }}
                    data-index={idx + 1}
                    aria-label={item.ariaLabel || `Go to ${item.label}`}
                  >
                    <span className="sm-panel-itemLabel inline-block origin-bottom will-change-transform">
                      {item.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Extra content (Profile, etc) */}
          {children && (
            <div className="sm-extra-content mt-auto pt-10 pb-8 border-t border-white/[0.06]">
              {children}
            </div>
          )}
        </div>
      </aside>

      <style>{`
.sm-scope .sm-prelayers {
  width: 100%;
}
.sm-scope .sm-panel {
  width: 100%;
}
.sm-scope .sm-panel-list[data-numbering] {
  counter-reset: smItem;
}
.sm-scope .sm-panel-list[data-numbering] .sm-panel-item::after {
  counter-increment: smItem;
  content: counter(smItem, decimal-leading-zero);
  position: absolute;
  top: 0.15em;
  right: 0;
  font-size: 14px;
  font-weight: 400;
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--sm-accent, #14b8a6);
  letter-spacing: 0.05em;
  pointer-events: none;
  user-select: none;
  opacity: var(--sm-num-opacity, 0);
}
.sm-scope .sm-panel-item:hover {
  color: var(--sm-accent, #14b8a6) !important;
}
      `}</style>
    </div>
  );
};

export default StaggeredMenu;
